import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { Button, Input } from '@zawarski/eti-ui';
import { Eye, EyeOff } from 'lucide-react';
import { clearCachedJwt, getCachedJwt, loginWithCognito } from './cognitoAuth';
import '@zawarski/eti-ui/styles.css';
import '../index.css';

interface LoginGateProps {
  /** Optional pre-supplied token (story arg / VITE_APP_JWT) - skips the form. */
  jwt?: string;
  children: (jwt: string) => ReactNode;
}

/**
 * Storybook-only username/password gate: authenticates against Cognito,
 * caches the id_token for the session, and hands it to the story.
 */
const LoginGate = ({ jwt: presetJwt, children }: LoginGateProps) => {
  const [jwt, setJwt] = useState<string | null>(() => presetJwt || getCachedJwt());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('theme-palmetto');
  }, []);

  useEffect(() => {
    if (presetJwt) setJwt(presetJwt);
  }, [presetJwt]);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!username.trim() || !password || loading) return;
    setLoading(true);
    setError(null);
    try {
      setJwt(await loginWithCognito(username, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    clearCachedJwt();
    setJwt(null);
    setPassword('');
  };

  if (jwt) {
    return (
      <div className='rep:flex rep:h-screen rep:w-full rep:flex-col'>
        <div className='rep:flex rep:items-center rep:justify-between rep:border-b rep:border-border rep:bg-muted rep:px-3 rep:py-1'>
          <span className='rep:text-xs rep:text-muted-foreground'>
            Storybook session · signed in with Cognito
          </span>
          <button
            type='button'
            onClick={signOut}
            className='rep:cursor-pointer rep:text-xs rep:font-medium rep:text-primary rep:hover:underline'>
            Sign out
          </button>
        </div>
        <div className='rep:min-h-0 rep:flex-1'>{children(jwt)}</div>
      </div>
    );
  }

  return (
    <div className='rep:flex rep:min-h-screen rep:w-full rep:items-center rep:justify-center rep:bg-background'>
      <form
        onSubmit={submit}
        className='rep:flex rep:w-full rep:max-w-sm rep:flex-col rep:gap-4 rep:rounded-xl rep:border rep:border-border rep:bg-card rep:p-6 rep:shadow-md'>
        <h2 className='rep:text-lg rep:font-semibold rep:text-foreground'>Palmetto REP Sign In</h2>
        <Input
          label='Username'
          value={username}
          required
          autoFocus
          autoComplete='username'
          onChange={(event) => setUsername(event.target.value)}
        />
        <Input
          label='Password'
          type={showPassword ? 'text' : 'password'}
          value={password}
          required
          autoComplete='current-password'
          onChange={(event) => setPassword(event.target.value)}
          endAdornment={
            <button
              type='button'
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}
              className='rep:cursor-pointer rep:text-muted-foreground rep:hover:text-foreground'>
              {showPassword ? <EyeOff className='rep:size-4' /> : <Eye className='rep:size-4' />}
            </button>
          }
        />
        {error ? <p className='rep:text-sm rep:text-destructive'>{error}</p> : null}
        <Button
          type='submit'
          className='rep:self-end'
          loading={loading}
          disabled={!username.trim() || !password || loading}>
          Sign In
        </Button>
      </form>
    </div>
  );
};

export default LoginGate;
